import React, {
  useMemo,
  useState,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useCallback,
} from "react";

import { useAppRouteParams } from "../../../AppRoutes";

import { getAPIErrorDetailAndMessage } from "@/api/helpers";
import { getMe } from "@/api/me";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import { Version } from "@/domain/Version";
import { Me, MeRaw } from "@/domain/me";
import {
  AllActionsType,
  PermissionsKey,
  PermissionsWithIdKey,
  defaultOrgRoles,
} from "@/domain/roles";
import { parsePermissions } from "@/helpers/permissions";
import { datadogRum } from "@datadog/browser-rum";

const permissionsCtx = createContext<{
  me: Me | null;
  isLoadedMe: boolean;
  runReloadMe: () => Promise<void>;
  hasPermission: (
    permission: PermissionsKey,
    action: AllActionsType
  ) => boolean;
  hasPermissionWithId: (
    permission: PermissionsWithIdKey,
    action: AllActionsType,
    id: string
  ) => boolean;
  hasVersionSheetPermission: (
    action: AllActionsType,
    id: { versionId: string; sheetId: string }
  ) => boolean;
  hasVersionsVersionCategoryActionPermission: (
    version: Version | null,
    action: AllActionsType
  ) => boolean;
  hasVersionActionPermission: (
    version: Version | null,
    action: AllActionsType
  ) => boolean;
  hasSheetActionPermission: (
    // 参照シートのように対象 version 本体への read 権限が無いケースでも
    // version_sheet:read:{vId}:{sId} の判定だけ通せるよう、最小限のフィールドで受け取れるようにする
    version: Pick<Version, "id" | "category"> | null,
    sheetId: string,
    action: AllActionsType
  ) => boolean;
  hasTadSheetActionPermission: (
    tadSheetId: string | null,
    action: AllActionsType
  ) => boolean;
}>({
  me: null,
  isLoadedMe: false,
  runReloadMe: async () => {},
  hasPermission: (permission, action) => false,
  hasPermissionWithId: (permission, action, id) => false,
  hasVersionsVersionCategoryActionPermission: (version, action) => false,
  hasVersionSheetPermission: (permission, action) => false,
  hasVersionActionPermission: (version, action) => false,
  hasSheetActionPermission: (version, sheetId, action) => false,
  hasTadSheetActionPermission: (tadSheetId, action) => false,
});

// 自身のメンバ情報およびロール情報を取得し各所に提供する
export const PermissionsProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { account } = useAppAccount();
  const { organizationId } = useAppRouteParams();
  const [me, setMe] = useState<Me | null>(null);
  const [isInProgress, setIsInProgress] = useState(true);
  const [_, setError] = useState<unknown>(null);

  const runReloadMe = useCallback(async () => {
    if (!account || !organizationId) return;
    setIsInProgress(true);
    try {
      const res: MeRaw = await getMe({ organizationId, uid: account.uid });
      const parsedPermissions = res.role
        ? parsePermissions(res.role.permissions)
        : null;
      const parsedMe: Me = {
        ...res,
        permissions:
          parsedPermissions?.permissions ?? defaultOrgRoles[res.member.role],
        customPermissions: parsedPermissions?.customPermissions ?? [],
      };
      setMe(parsedMe);
    } catch (err) {
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    } finally {
      setIsInProgress(false);
    }
  }, [account, organizationId]);

  // ユーザー自身が「permissionに」「actionsを」行う権限を持っているかどうかを判定する
  const hasPermission = useCallback(
    (permission: PermissionsKey, action: AllActionsType) => {
      if (!me) return false;
      return me.permissions[permission][action] ?? false;
    },
    [me]
  );

  // ユーザー自身が「id付きpermissionに」「actionsを」行う権限を持っているかどうかを判定する
  const hasPermissionWithId = useCallback(
    (permission: PermissionsWithIdKey, action: AllActionsType, id: string) => {
      if (!me) return false;
      return me.permissions[permission][action].includes(id);
    },
    [me]
  );

  // version_sheetの権限を持っているかどうかを判定する
  // Object型の判定を行うため、findを使用
  // ref: https://github.com/algo-artis/spreadsheet-ui-frontend/pull/1686
  const hasVersionSheetPermission = useCallback(
    (
      action: AllActionsType,
      sheetVersionIdentifiers: { versionId: string; sheetId: string }
    ) => {
      if (!me) return false;
      return !!me.permissions.version_sheet[action].find(
        (p) =>
          p.versionId === sheetVersionIdentifiers.versionId &&
          p.sheetId === sheetVersionIdentifiers.sheetId
      );
    },
    [me]
  );

  // 任意のカテゴリに対しての任意のアクションの権限判定
  const hasVersionsVersionCategoryActionPermission = useCallback(
    (version: Version | null, action: AllActionsType) => {
      if (!version) return false;
      return (
        hasPermission("versions", action) ||
        hasPermissionWithId(
          "versions_version_category",
          action,
          version.category
        )
      );
    },
    [hasPermission, hasPermissionWithId]
  );

  // 任意のバージョンに対しての任意のアクションの権限判定
  const hasVersionActionPermission = useCallback(
    (version: Version | null, action: AllActionsType) => {
      if (!version) return false;
      return (
        hasPermission("versions", action) ||
        hasPermissionWithId(
          "versions_version_category",
          action,
          version.category
        ) ||
        hasPermissionWithId("version", action, version.id)
      );
    },
    [hasPermission, hasPermissionWithId]
  );

  // 任意のシートに対しての任意のアクションの権限判定
  const hasSheetActionPermission = useCallback(
    (
      version: Pick<Version, "id" | "category"> | null,
      sheetId: string,
      action: AllActionsType
    ) => {
      if (!version) return false;
      return (
        hasPermission("versions", action) ||
        hasPermissionWithId(
          "versions_version_category",
          action,
          version.category ?? ""
        ) ||
        hasPermissionWithId("version", action, version.id) ||
        hasPermissionWithId("sheet", action, sheetId) ||
        hasVersionSheetPermission(action, { versionId: version.id, sheetId })
      );
    },
    [hasPermission, hasPermissionWithId, hasVersionSheetPermission]
  );

  // 任意の TADS に対しての任意のアクションの権限判定
  // (メタデータ操作にパラメトリック版は無いので tad_sheets_meta:write を直接使うこと)
  const hasTadSheetActionPermission = useCallback(
    (tadSheetId: string | null, action: AllActionsType) => {
      if (!tadSheetId) return false;
      return (
        hasPermission("tad_sheets", action) ||
        hasPermissionWithId("tad_sheet", action, tadSheetId)
      );
    },
    [hasPermission, hasPermissionWithId]
  );

  useEffect(() => {
    runReloadMe();
  }, [runReloadMe]);

  useEffect(() => {
    datadogRum.setUserProperty("organizationId", organizationId);
    datadogRum.setUserProperty("role", me?.member.role ?? "null");
  }, [me]);

  const permissionsCtxValue = useMemo(
    () => ({
      me,
      isLoadedMe: !isInProgress,
      runReloadMe,
      hasPermission,
      hasPermissionWithId,
      hasVersionsVersionCategoryActionPermission,
      hasVersionSheetPermission,
      hasVersionActionPermission,
      hasSheetActionPermission,
      hasTadSheetActionPermission,
    }),
    [
      me,
      isInProgress,
      runReloadMe,
      hasPermission,
      hasPermissionWithId,
      hasVersionsVersionCategoryActionPermission,
      hasVersionSheetPermission,
      hasVersionActionPermission,
      hasSheetActionPermission,
      hasTadSheetActionPermission,
    ]
  );

  return (
    <permissionsCtx.Provider value={permissionsCtxValue}>
      {children}
    </permissionsCtx.Provider>
  );
};

export const usePermissions = () => {
  return useContext(permissionsCtx);
};
